import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import Logo from "../components/Logo";
import ThemeToggle from "../components/ThemeToggle";
import { Lock, LogOut, Search, RefreshCcw, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "../hooks/use-toast";
import { useAdminSession } from "../hooks/use-admin-session";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminEnquiries() {
  const { authenticated, checkingSession, authLoading, signIn, signOut, adminSessionConfig } = useAdminSession();
  const [inputToken, setInputToken] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  const logout = useCallback(async () => {
    await signOut();
    setInputToken("");
    setItems([]);
  }, [signOut]);

  const fetchEnquiries = useCallback(async () => {
    if (!authenticated) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/enquiries`, adminSessionConfig);
      setItems(res.data || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        await logout();
        toast({ title: "Session expired", description: "Please sign in again." });
      } else {
        toast({ title: "Failed to load", description: "Could not fetch enquiries." });
      }
    } finally {
      setLoading(false);
    }
  }, [adminSessionConfig, authenticated, logout]);

  useEffect(() => {
    if (authenticated) fetchEnquiries();
  }, [authenticated, fetchEnquiries]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return items;
    return items.filter((item) =>
      [item.name, item.email, item.organization, item.phone, item.message, item.source, item.request_type]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(query))
    );
  }, [items, q]);

  const login = async (e) => {
    e.preventDefault();
    const ok = await signIn(inputToken);
    if (ok) {
      setInputToken("");
      toast({ title: "Signed in", description: "Loading enquiries…" });
    }
  };

  if (checkingSession) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F5F7FB] text-[#0B1B3B]"><div className="inline-flex items-center gap-3 text-sm font-semibold"><Loader2 size={18} className="animate-spin" /> Checking admin session…</div></div>;
  }

  if (!authenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F7FB] p-6">
        <form onSubmit={login} className="w-full max-w-md bg-white rounded-2xl border border-slate-200 p-8 shadow-xl">
          <div className="flex items-center justify-between mb-6">
            <Logo variant="light" size={40} />
            <ThemeToggle />
          </div>
          <div className="h-12 w-12 rounded-xl bg-[#0B1B3B] text-white flex items-center justify-center"><Lock size={20} /></div>
          <h1 className="mt-4 text-2xl font-bold text-[#0B1B3B]">Enquiries admin</h1>
          <p className="mt-2 text-sm text-slate-600">Enter your admin token to see preview enquiries.</p>
          <label className="block mt-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Admin Token</label>
          <input data-testid="admin-enquiries-token-input" type="password" value={inputToken} onChange={(e) => setInputToken(e.target.value)} placeholder="Paste your token…" className="mt-2 w-full border border-slate-200 rounded-lg px-4 py-3 outline-none focus:border-[#1E56E3] text-sm" />
          <button data-testid="admin-enquiries-signin-button" type="submit" disabled={authLoading} className="btn-primary mt-5 w-full justify-center disabled:opacity-70">
            {authLoading ? (<><Loader2 size={16} className="animate-spin" /> Verifying…</>) : ("Sign In")}
          </button>
          <div className="mt-6 text-center">
            <Link to="/" className="text-xs text-slate-500 hover:text-[#1E56E3] inline-flex items-center gap-1"><ArrowLeft size={12} /> Back to site</Link>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F7FB]">
      <div className="bg-white border-b border-slate-200">
        <div className="container-x flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <Logo variant="light" size={36} />
            <span className="hidden md:inline text-xs font-semibold uppercase tracking-wider text-slate-500 border border-slate-200 rounded-full px-3 py-1">Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <button data-testid="admin-enquiries-signout-button" onClick={logout} className="inline-flex items-center gap-2 text-sm font-semibold text-[#0B1B3B] hover:text-[#1E56E3] px-3 py-2"><LogOut size={14} /> Sign out</button>
          </div>
        </div>
      </div>

      <div className="container-x py-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <span className="tag-chip">Preview Only</span>
            <h1 className="section-title mt-3">Saved enquiries</h1>
            <p className="text-sm text-slate-500 mt-2">Simple admin view for enquiries stored in MongoDB.</p>
          </div>
          <button data-testid="admin-enquiries-refresh-button" onClick={fetchEnquiries} className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-full border border-slate-200 hover:border-[#1E56E3] hover:text-[#1E56E3] self-start md:self-auto"><RefreshCcw size={14} /> Refresh</button>
        </div>

        <div className="mt-8 grid sm:grid-cols-3 gap-4">
          <div className="rounded-2xl bg-white border border-slate-200 p-6">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Total Enquiries</div>
            <div className="mt-3 text-4xl font-extrabold text-[#0B1B3B]" data-testid="admin-enquiries-total-count">{items.length}</div>
          </div>
          <div className="rounded-2xl bg-white border border-slate-200 p-6">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Matching Filter</div>
            <div className="mt-3 text-4xl font-extrabold text-[#0B1B3B]">{filtered.length}</div>
          </div>
          <div className="rounded-2xl bg-white border border-slate-200 p-6">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Latest</div>
            <div className="mt-3 text-sm font-semibold text-[#0B1B3B]">{items[0] ? new Date(items[0].created_at).toLocaleString() : "—"}</div>
          </div>
        </div>

        <div className="mt-8 relative max-w-xl">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="admin-enquiries-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email, org, type…" className="w-full pl-11 pr-4 py-3 rounded-full border border-slate-200 bg-white text-sm outline-none focus:border-[#1E56E3]" />
        </div>

        <div className="mt-6 space-y-4" data-testid="admin-enquiries-list">
          {loading && <div className="rounded-2xl bg-white border border-slate-200 px-5 py-10 text-center text-slate-500"><Loader2 size={16} className="animate-spin inline mr-2" /> Loading…</div>}
          {!loading && filtered.length === 0 && <div className="rounded-2xl bg-white border border-slate-200 px-5 py-10 text-center text-slate-500">No enquiries yet.</div>}
          {!loading && filtered.map((item) => (
            <div key={item.id} className="rounded-2xl bg-white border border-slate-200 p-5 shadow-sm">
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div>
                  <div className="text-lg font-bold text-[#0B1B3B]">{item.name}</div>
                  <div className="mt-1 text-sm text-slate-600">{item.organization || "—"}</div>
                  <a href={`mailto:${item.email}`} className="mt-2 inline-flex text-sm font-semibold text-[#1E56E3] hover:underline">{item.email}</a>
                </div>
                <div className="flex flex-wrap gap-2 md:justify-end">
                  <span className="text-[10px] font-bold uppercase tracking-wider bg-[#EEF3FF] text-[#1E56E3] px-2 py-1 rounded">{item.request_type || "enquiry"}</span>
                  <span className="text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600 px-2 py-1 rounded">{item.source || "contact_form"}</span>
                </div>
              </div>
              <div className="mt-4 grid gap-4 md:grid-cols-[160px_1fr] text-sm">
                <div className="text-slate-500">{new Date(item.created_at).toLocaleString()}</div>
                <div className="space-y-2 text-slate-700">
                  <div><span className="font-semibold text-[#0B1B3B]">Phone:</span> {item.phone || "—"}</div>
                  <div><span className="font-semibold text-[#0B1B3B]">Message:</span> {item.message || "—"}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}